// Modal de convite por e-mail (PO1-2046).
// Padrão: modal-align-top + fade={false} + div header customizado.

import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import { createInvites } from "services/invitesApi";
import { listRoles } from "services/rolesApi";
import { useToast } from "hooks/toast";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(raw) {
  return Array.from(
    new Set(
      String(raw || "")
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    )
  );
}

const REASON_LABEL = {
  empty: "vazio",
  invalid: "formato inválido",
  user_exists: "já existe um usuário com este e-mail",
  storage_error: "falha ao gravar convite",
  email_failed: "falha ao enviar e-mail",
};

export default function InviteUsersModal({
  isOpen,
  onClose,
  onCreated,
  idEmbarcador,
  embarcadorLabel,
}) {
  const [emailsRaw, setEmailsRaw] = useState("");
  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const { addToast } = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setEmailsRaw("");
    setSelectedRoleIds([]);
    setErrors({});
    (async () => {
      try {
        const list = await listRoles();
        setRoles(Array.isArray(list) ? list : list.items || []);
      } catch (err) {
        console.error("[InviteUsersModal] listRoles:", err);
      }
    })();
  }, [isOpen]);

  const toggleRole = useCallback((roleId) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    const emails = parseEmails(emailsRaw);
    const nextErrors = {};
    if (emails.length === 0) {
      nextErrors.emails = "Informe ao menos um e-mail";
    } else {
      const invalid = emails.filter((e) => !EMAIL_REGEX.test(e));
      if (invalid.length) {
        nextErrors.emails = `E-mails inválidos: ${invalid.join(", ")}`;
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    try {
      const result = await createInvites({
        emails,
        roleIds: selectedRoleIds,
        idEmbarcador,
      });
      const createdCount = (result.created || []).length;
      const skippedCount = (result.skipped || []).length;

      if (createdCount) {
        addToast({
          place: "tr",
          type: "success",
          title: `${createdCount} convite${createdCount > 1 ? "s" : ""} enviado${createdCount > 1 ? "s" : ""}`,
          description:
            skippedCount > 0
              ? `${skippedCount} ignorado${skippedCount > 1 ? "s" : ""} — verifique a lista`
              : undefined,
          icon: "tim-icons icon-email-85",
        });
      }
      if (skippedCount && !createdCount) {
        const reasons = (result.skipped || [])
          .map((s) => `${s.email}: ${REASON_LABEL[s.reason] || s.reason}`)
          .join("\n");
        addToast({
          place: "tr",
          type: "danger",
          title: "Nenhum convite enviado",
          description: reasons,
          icon: "tim-icons icon-alert-circle-exc",
        });
      }
      if (onCreated) onCreated(result);
      if (createdCount) onClose();
    } catch (err) {
      console.error("[InviteUsersModal] submit:", err);
      const data = (err.response && err.response.data) || {};
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: data.error || "Falha ao enviar convites",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSubmitting(false);
    }
  }, [emailsRaw, selectedRoleIds, idEmbarcador, addToast, onClose, onCreated]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="md"
      modalClassName="modal-align-top"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: AUTOPILOT_COLORS.insetSurface,
          borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          padding: "16px 20px",
        }}
      >
        <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: 15 }}>
          Convidar usuários por e-mail
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: AUTOPILOT_COLORS.title,
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 4px",
          }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody>
        {embarcadorLabel ? (
          <div
            style={{
              marginBottom: 14,
              padding: "8px 12px",
              borderRadius: 8,
              background: "var(--theme-accent-glow)",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              fontSize: 13,
              color: AUTOPILOT_COLORS.title,
            }}
          >
            Os convites serão associados ao embarcador <strong>{embarcadorLabel}</strong>.
          </div>
        ) : null}
        <FormGroup>
          <Label>E-mails</Label>
          <Input
            type="textarea"
            rows={5}
            placeholder={"Cole um ou mais e-mails (separados por vírgula, ponto-e-vírgula ou quebra de linha)\n\nex.: maria@empresa.com, joao@empresa.com"}
            value={emailsRaw}
            onChange={(e) => setEmailsRaw(e.target.value)}
            invalid={!!errors.emails}
          />
          {errors.emails && (
            <small style={{ color: AUTOPILOT_COLORS.statusDanger }}>{errors.emails}</small>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Grupos (papéis) — opcional</Label>
          <div
            style={{
              maxHeight: 180,
              overflowY: "auto",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: 4,
              padding: "8px 12px",
            }}
          >
            {roles.length === 0 && (
              <small style={{ color: AUTOPILOT_COLORS.muted }}>
                Nenhum papel disponível
              </small>
            )}
            {roles.map((r) => (
              <div key={r.Id} style={{ marginBottom: 4 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: 13,
                    color: AUTOPILOT_COLORS.title,
                  }}
                >
                  <input
                    type="checkbox"
                    style={{ marginRight: 8 }}
                    checked={selectedRoleIds.includes(r.Id)}
                    onChange={() => toggleRole(r.Id)}
                  />
                  {r.DisplayName || r.Name}
                </label>
              </div>
            ))}
          </div>
          <small style={{ color: AUTOPILOT_COLORS.muted }}>
            Os papéis selecionados serão atribuídos ao usuário após ele aceitar o convite.
          </small>
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Enviando..." : "Enviar convites"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
